import { darkThemeBodyBackground } from "../../../services/ThemeService";
import React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens, Text, Tooltip, mergeClasses, Skeleton, SkeletonItem } from "@fluentui/react-components";
import { MailCheckmark20Regular } from "@fluentui/react-icons";
import { Logger } from "../../../services/Logger";
import { EmailListItem } from "../../../models/EmailListResponse";
import { NewformaSpinner } from "../../_base/Spinner";
import { NewformaCheckbox } from "../../_base/Checkbox";
import moment from "moment";
import { useTheme } from "../../../hooks/useTheme";

const EMAIL_TOOLTIP_SHOW_DELAY_MS = 250;
const EMAIL_PREFETCH_THRESHOLD_COUNT = 20;

// Inline style (not makeStyles) because griffel can drop the vendor-prefixed
// `-webkit-box` / `-webkit-box-orient` values required for multi-line ellipsis clamping.
// Clamps the hover tooltip's body text and appends an ellipsis when it overflows.
const bodyTooltipContentStyle: React.CSSProperties = {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 6,
    overflow: "hidden",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
};

type DelayedEmailTooltipProps = React.ComponentProps<typeof Tooltip>;

const DelayedEmailTooltip: React.FC<DelayedEmailTooltipProps> = ({ children, ...props }) => {
    const [visible, setVisible] = React.useState(false);
    const showTimerRef = React.useRef<number | null>(null);

    const clearShowTimer = React.useCallback(() => {
        if (showTimerRef.current) {
            window.clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
    }, []);

    const showAfterDelay = React.useCallback(() => {
        clearShowTimer();
        showTimerRef.current = window.setTimeout(() => setVisible(true), EMAIL_TOOLTIP_SHOW_DELAY_MS);
    }, [clearShowTimer]);

    const hideImmediately = React.useCallback(() => {
        clearShowTimer();
        setVisible(false);
    }, [clearShowTimer]);

    React.useEffect(() => clearShowTimer, [clearShowTimer]);

    // A hover tooltip has no `pointerleave` to react to when the user scrolls with the
    // wheel/trackpad, so its anchored row slides away and the tooltip appears to "fly".
    // While visible, dismiss it on any scroll or wheel. `scroll` does not bubble, so it is
    // captured (phase) to catch scrolls from the inner list container; `wheel` bubbles and
    // fires before the scroll, covering trackpad gestures. The listeners are only attached
    // while the tooltip is visible, so at most one tooltip is ever subscribed.
    React.useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const hide = () => hideImmediately();
        document.addEventListener("scroll", hide, true);
        window.addEventListener("wheel", hide, { passive: true });
        return () => {
            document.removeEventListener("scroll", hide, true);
            window.removeEventListener("wheel", hide);
        };
    }, [visible, hideImmediately]);

    if (!React.isValidElement(children)) {
        return null;
    }

    const child = children as React.ReactElement<any>;

    const trigger = React.cloneElement(child, {
        onPointerEnter: (event: React.PointerEvent<HTMLElement>) => {
            child.props.onPointerEnter?.(event);
            showAfterDelay();
        },
        onPointerLeave: (event: React.PointerEvent<HTMLElement>) => {
            child.props.onPointerLeave?.(event);
            hideImmediately();
        },
        onFocus: (event: React.FocusEvent<HTMLElement>) => {
            child.props.onFocus?.(event);
            showAfterDelay();
        },
        onBlur: (event: React.FocusEvent<HTMLElement>) => {
            child.props.onBlur?.(event);
            hideImmediately();
        },
    });

    return (
        <Tooltip {...props} visible={visible} showDelay={0} hideDelay={0}>
            {trigger}
        </Tooltip>
    );
};

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        userSelect: "none",
    },
    listContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 0,
    },
    emptyState: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: tokens.spacingVerticalL,
    },
    loadingContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
    },
    skeletonContainer: {
        display: "flex",
        flexDirection: "column",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    skeletonRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: "6px",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    skeletonCheckboxColumn: {
        width: "24px",
        minWidth: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
    },
    skeletonCheckbox: {
        width: "16px",
        height: "16px",
        borderRadius: "3px",
    },
    skeletonFiledIcon: {
        width: "20px",
        height: "20px",
        borderRadius: "50%",
    },
    skeletonContent: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "7px",
    },
    skeletonSenderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.spacingHorizontalS,
    },
    skeletonSender: {
        width: "44%",
        height: "14px",
        borderRadius: tokens.borderRadiusSmall,
    },
    skeletonDate: {
        width: "56px",
        height: "11px",
        borderRadius: tokens.borderRadiusSmall,
    },
    skeletonSubject: {
        width: "74%",
        height: "13px",
        borderRadius: tokens.borderRadiusSmall,
    },
    skeletonPreview: {
        width: "92%",
        height: "11px",
        borderRadius: tokens.borderRadiusSmall,
    },
    loadingMoreContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80px",
        padding: "12px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: "6px",
        boxSizing: "border-box",
        backgroundColor: tokens.colorNeutralBackground1,
        "& .newforma-loadingEmailsIndicator": {
            margin: 0,
            paddingTop: 0,
        },
        "& .newforma-loadingEmailsIndicator .ms-Spinner": {
            flexDirection: "row",
            alignItems: "center",
        },
        "& .newforma-loadingEmailsIndicator .ms-Spinner-label": {
            margin: "0 0 0 8px",
        },
        "& .newforma-loadingEmailsIndicator br": {
            display: "none",
        },
    },
    loadMoreSentinel: {
        height: "1px",
    },
    endOfListRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "12px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        color: tokens.colorNeutralForeground3,
        backgroundColor: tokens.colorNeutralBackground1,
        fontSize: "12px",
        userSelect: "none",
    },
    emailRow: {
        display: "flex",
        alignItems: "stretch",
        gap: "12px",
        padding: "12px 10px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        transition: "background-color 0.2s",
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    emailRowSelected: {
        backgroundColor: tokens.colorNeutralBackground1Selected,
        backgroundImage: `linear-gradient(90deg, ${tokens.colorNeutralBackground1} 0%, ${tokens.colorBrandBackground2Hover} 50%, ${tokens.colorNeutralBackground1} 100%)`,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Selected,
            backgroundImage: `linear-gradient(90deg, ${tokens.colorNeutralBackground1Hover} 0%, ${tokens.colorBrandBackground2Hover} 50%, ${tokens.colorNeutralBackground1Hover} 100%)`,
        },
    },
    emailRowSelectedDark: {
        backgroundColor: darkThemeBodyBackground,
        backgroundImage:
            "linear-gradient(90deg, rgba(38, 119, 204, 0) 0%, rgba(38, 119, 204, 0.162) 25%, rgba(38, 119, 204, 0.306) 50%, rgba(38, 119, 204, 0.162) 75%, rgba(38, 119, 204, 0) 100%)",
        ":hover": {
            backgroundColor: darkThemeBodyBackground,
            backgroundImage:
                "linear-gradient(90deg, rgba(38, 119, 204, 0) 0%, rgba(38, 119, 204, 0.198) 25%, rgba(38, 119, 204, 0.36) 50%, rgba(38, 119, 204, 0.198) 75%, rgba(38, 119, 204, 0) 100%)",
        },
    },
    emailContent: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        userSelect: "text",
    },
    senderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
    },
    senderName: {
        fontWeight: tokens.fontWeightBold,
        fontSize: "14px",
        lineHeight: "1.4",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    emailSubject: {
        fontWeight: tokens.fontWeightRegular,
        fontSize: "13px",
        lineHeight: "1.4",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    emailDate: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
        whiteSpace: "nowrap",
    },
    emailPreview: {
        fontSize: "11px",
        color: tokens.colorNeutralForeground3,
        lineHeight: "1.3",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    checkboxColumn: {
        width: "24px",
        minWidth: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "10px",
        paddingTop: 0,
    },
    checkboxRoot: {
        margin: 0,
    },
    filedIcon: {
        color: tokens.colorPaletteGreenForeground1,
        fontSize: "20px",
        lineHeight: "20px",
    },
    subjectRow: {
        display: "flex",
        alignItems: "center",
        gap: 0,
    },
    checkboxSpacing: {
        width: "20px",
        minWidth: "20px",
    },
    highlight: {
        backgroundColor: tokens.colorPaletteYellowBackground2,
        color: "inherit",
        display: "inline",
        padding: "0 1px",
    },
});

interface EmailListFluent9OwnProps {
    emails: EmailListItem[];
    selectedEmailIds: ReadonlySet<string>;
    pagingItemCount: number;
    onEmailSelectionChanged: (emailId: string, isSelected: boolean) => void;
    isLoading?: boolean;
    isLoadingMore?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => Promise<void>;
    scrollableTargetId?: string;
    disabled?: boolean;
    searchFilter?: string;
    isReloading?: boolean;
    logger?: Logger;
}

interface EmailListFluent9Props extends EmailListFluent9OwnProps, LocalizeContextProps {}

interface EmailRowProps {
    email: EmailListItem;
    isSelected: boolean;
    disabled: boolean;
    searchFilter: string;
    styles: ReturnType<typeof useStyles>;
    filedTooltipText: string;
    onEmailSelectionChanged: (emailId: string, isSelected: boolean) => void;
}
const formatDate = (dateString: string): string => {
    const basicFormat = "YYYY-MM-DD";
    return moment(dateString).format(basicFormat);
};

const filedCategory = "Filed by Newforma";
// The filed indicator is derived only from the Outlook category string,
// but the filing flow updates email.isFiled (and categories may be absent or localized).
// As a result, newly filed emails (when not deleted) may not show as filed until reload.
const hasFiledCategory = (email: EmailListItem): boolean =>
    email.isFiled === true || (Array.isArray(email.categories) && email.categories.includes(filedCategory));

const getSenderDisplayName = (email: EmailListItem): string => email.from?.name || email.from?.address || "";

const renderHighlightedText = (
    value: string | undefined,
    searchFilter: string,
    highlightClassName: string
): React.ReactNode => {
    const text = value ?? "";
    const filter = searchFilter.trim();
    if (!filter) {
        return text;
    }

    const textLower = text.toLowerCase();
    const filterTerms = Array.from(
        new Set(
            filter
                .toLowerCase()
                .split(/\s+/)
                .map((term) => term.replace(/^[^a-z0-9]+|[^a-z0-9]+$/gi, ""))
                .filter(Boolean)
        )
    );
    if (!filterTerms.length) {
        return text;
    }

    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    while (currentIndex < text.length) {
        const nextMatch = filterTerms.reduce<{ index: number; term: string } | null>((bestMatch, term) => {
            const index = textLower.indexOf(term, currentIndex);
            if (index < 0) {
                return bestMatch;
            }
            if (
                !bestMatch ||
                index < bestMatch.index ||
                (index === bestMatch.index && term.length > bestMatch.term.length)
            ) {
                return { index, term };
            }
            return bestMatch;
        }, null);

        if (!nextMatch) {
            break;
        }

        const matchIndex = nextMatch.index;
        if (matchIndex > currentIndex) {
            parts.push(text.substring(currentIndex, matchIndex));
        }

        const matchEndIndex = matchIndex + nextMatch.term.length;
        parts.push(
            <span className={highlightClassName} key={`${matchIndex}-${matchEndIndex}`}>
                {text.substring(matchIndex, matchEndIndex)}
            </span>
        );

        currentIndex = matchEndIndex;
    }

    if (currentIndex < text.length) {
        parts.push(text.substring(currentIndex));
    }

    return parts.length ? parts : text;
};

const EmailRow = React.memo<EmailRowProps>(
    ({ email, isSelected, disabled, searchFilter, styles, filedTooltipText, onEmailSelectionChanged }) => {
        const { isDarkMode } = useTheme();
        const senderDisplayName = getSenderDisplayName(email);
        const handleCheckboxChange = (_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean | "mixed" }) => {
            onEmailSelectionChanged(email.id, data.checked === true);
        };

        return (
            <div
                className={mergeClasses(
                    "newforma-emailListRowItemComponentFluent9",
                    styles.emailRow,
                    isSelected && styles.emailRowSelected,
                    isSelected && isDarkMode && styles.emailRowSelectedDark
                )}
            >
                <div className={styles.checkboxColumn}>
                    <span className={styles.checkboxRoot} onClick={(e) => e.stopPropagation()}>
                        <NewformaCheckbox
                            checked={isSelected}
                            disabled={disabled}
                            onChange={handleCheckboxChange}
                            ariaLabel={email.subject}
                        />
                    </span>
                    {hasFiledCategory(email) ? (
                        <DelayedEmailTooltip
                            content={filedTooltipText}
                            relationship="label"
                            appearance="inverted"
                            withArrow
                        >
                            <MailCheckmark20Regular
                                className={styles.filedIcon}
                                onClick={(event) => event.stopPropagation()}
                            />
                        </DelayedEmailTooltip>
                    ) : (
                        <span className={styles.checkboxSpacing} />
                    )}
                </div>
                <div className={styles.emailContent}>
                    <div className={styles.senderRow}>
                        <DelayedEmailTooltip
                            content={senderDisplayName}
                            relationship="description"
                            appearance="inverted"
                            withArrow
                        >
                            <Text className={styles.senderName}>
                                {renderHighlightedText(senderDisplayName, searchFilter, styles.highlight)}
                            </Text>
                        </DelayedEmailTooltip>
                        <Text className={styles.emailDate}>{formatDate(email.sentDateTime)}</Text>
                    </div>
                    <div className={styles.subjectRow}>
                        <DelayedEmailTooltip
                            content={email.subject || ""}
                            relationship="description"
                            appearance="inverted"
                            withArrow
                        >
                            <Text className={styles.emailSubject}>
                                {renderHighlightedText(email.subject, searchFilter, styles.highlight)}
                            </Text>
                        </DelayedEmailTooltip>
                    </div>
                    <DelayedEmailTooltip
                        content={<span style={bodyTooltipContentStyle}>{email.bodyPreview || ""}</span>}
                        relationship="description"
                        appearance="inverted"
                        withArrow
                    >
                        <Text className={styles.emailPreview}>
                            {renderHighlightedText(email.bodyPreview, searchFilter, styles.highlight)}
                        </Text>
                    </DelayedEmailTooltip>
                </div>
            </div>
        );
    },
    (prevProps, nextProps) =>
        prevProps.email === nextProps.email &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.searchFilter === nextProps.searchFilter &&
        prevProps.styles === nextProps.styles &&
        prevProps.filedTooltipText === nextProps.filedTooltipText &&
        prevProps.onEmailSelectionChanged === nextProps.onEmailSelectionChanged
);

EmailRow.displayName = "EmailRow";

const EmailListFluent9Inner: React.FC<EmailListFluent9Props> = ({
    emails,
    selectedEmailIds,
    pagingItemCount,
    onEmailSelectionChanged,
    isLoading = false,
    isLoadingMore = false,
    hasMore = false,
    onLoadMore,
    scrollableTargetId,
    disabled = false,
    searchFilter = "",
    isReloading = false,
    translate,
    logger,
}) => {
    const styles = useStyles();
    const loadMoreRequestRef = React.useRef(false);
    const emailsLengthRef = React.useRef(emails.length);
    const hasMoreRef = React.useRef(hasMore);
    const isLoadingMoreRef = React.useRef(isLoadingMore);
    const onLoadMoreRef = React.useRef(onLoadMore);

    emailsLengthRef.current = emails.length;
    hasMoreRef.current = hasMore;
    isLoadingMoreRef.current = isLoadingMore;
    onLoadMoreRef.current = onLoadMore;

    const loadMoreEmails = React.useCallback(async () => {
        const latestOnLoadMore = onLoadMoreRef.current;
        if (!hasMoreRef.current || isLoadingMoreRef.current || !latestOnLoadMore || loadMoreRequestRef.current) {
            return;
        }

        loadMoreRequestRef.current = true;
        try {
            await latestOnLoadMore();
        } finally {
            loadMoreRequestRef.current = false;
        }
    }, []);

    React.useEffect(() => {
        const scrollableElement = scrollableTargetId ? document.getElementById(scrollableTargetId) : null;
        if (!scrollableElement) {
            return;
        }

        const handleScroll = () => {
            const remainingScrollPixels =
                scrollableElement.scrollHeight - scrollableElement.scrollTop - scrollableElement.clientHeight;

            if (!emailsLengthRef.current || !hasMoreRef.current || isLoadingMoreRef.current || !onLoadMoreRef.current) {
                return;
            }

            const averageEmailRowHeight = scrollableElement.scrollHeight / emailsLengthRef.current;
            const remainingEmailCount = remainingScrollPixels / averageEmailRowHeight;
            if (remainingEmailCount <= EMAIL_PREFETCH_THRESHOLD_COUNT) {
                void loadMoreEmails();
            }
        };

        handleScroll();
        scrollableElement.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            scrollableElement.removeEventListener("scroll", handleScroll);
        };
    }, [emails.length, hasMore, isLoadingMore, loadMoreEmails, onLoadMore, scrollableTargetId]);

    const EmailListSkeleton = (): JSX.Element => (
        <div className={styles.skeletonContainer} aria-busy="true" aria-label="Loading emails">
            {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className={styles.skeletonRow}>
                    <div className={styles.skeletonCheckboxColumn}>
                        <SkeletonItem className={styles.skeletonCheckbox} />
                        <SkeletonItem className={styles.skeletonFiledIcon} />
                    </div>
                    <div className={styles.skeletonContent}>
                        <div className={styles.skeletonSenderRow}>
                            <SkeletonItem
                                className={styles.skeletonSender}
                                style={{ width: `${38 + (index % 3) * 8}%` }}
                            />
                            <SkeletonItem className={styles.skeletonDate} />
                        </div>
                        <SkeletonItem
                            className={styles.skeletonSubject}
                            style={{ width: `${62 + (index % 2) * 12}%` }}
                        />
                        <SkeletonItem
                            className={styles.skeletonPreview}
                            style={{ width: `${82 + (index % 3) * 5}%` }}
                        />
                    </div>
                </Skeleton>
            ))}
        </div>
    );

    if (isLoading && isReloading) {
        return <EmailListSkeleton />;
    }

    if (isLoading) {
        return (
            <div className={styles.loadingContainer}>
                <NewformaSpinner
                    className="newforma-loadingEmailsIndicator"
                    label={translate("FILE_MULTIPLE_EMAIL.LOADING_EMAILS") as string}
                />
            </div>
        );
    }

    if (!emails || emails.length === 0) {
        return (
            <div className={styles.emptyState}>
                <Text>{translate("FILE_MULTIPLE_EMAIL.NO_EMAILS") as string}</Text>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={styles.listContainer}>
                {emails.map((email) => (
                    <EmailRow
                        key={email.id}
                        email={email}
                        isSelected={selectedEmailIds.has(email.id)}
                        disabled={disabled}
                        searchFilter={searchFilter}
                        styles={styles}
                        filedTooltipText={translate("FILE_MULTIPLE_EMAIL.FILED_BY_NEWFORMA") as string}
                        onEmailSelectionChanged={onEmailSelectionChanged}
                    />
                ))}
                {isLoadingMore ? (
                    <div className={styles.loadingMoreContainer}>
                        <NewformaSpinner
                            className="newforma-loadingEmailsIndicator"
                            label={translate("FILE_MULTIPLE_EMAIL.LOADING_EMAILS") as string}
                        />
                    </div>
                ) : null}
                {!hasMore ? (
                    <div className={styles.endOfListRow} role="presentation" aria-hidden="true">
                        <Text>{translate("FILE_MULTIPLE_EMAIL.END_OF_LIST") as string}</Text>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default withLocalize(EmailListFluent9Inner);
